import {
  LayoutDashboard,
  CircleUserRound,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
  LogOut,
  Settings,
  Building2,
  ScanSearch,
  ChartLine,
} from 'lucide-react';
import { NavLink, Link } from 'react-router-dom';
import { KollabHRFullLogo, KollabHRLogo } from '@gokrew/ui/assets';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@gokrew/ui/hooks';
import { useCallback, useEffect } from 'react';
import { cn } from '@gokrew/ui/lib/utils';
import { Button, ConfirmationDialog, useDialog } from '@gokrew/ui';
import { useAuth } from '@/context/AuthContext';
import styles from './Sidebar.module.css';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

interface MenuItem {
  name: string;
  icon: LucideIcon;
  href: string;
  end?: boolean;
}

export function Sidebar({ isCollapsed, onToggle }: SidebarProps) {
  const { t } = useTranslation('sidebar');
  const isMobile = useMediaQuery('(max-width: 47.999rem)');
  const { logout, user } = useAuth();
  const { openDialog } = useDialog();
  const isSuperAdmin = user?.role === 'super_admin';

  const openLogoutConfirmationDialog = useCallback(
    () =>
      openDialog({
        title: t('logout_title'),
        content: (
          <ConfirmationDialog onSubmit={logout}>
            <p>{t('logout_confirmation')}</p>
          </ConfirmationDialog>
        ),
      }),
    [logout, openDialog, t]
  );

  const handleNavigation = useCallback(() => {
    if (isMobile && !isCollapsed) onToggle();
  }, [isMobile, isCollapsed, onToggle]);

  useEffect(() => {
    if (!isMobile || isCollapsed) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onToggle();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobile, isCollapsed, onToggle]);

  const navigation: MenuItem[] = [
    ...(isSuperAdmin
      ? [{ name: t('dashboard'), icon: LayoutDashboard, href: '/', end: true }]
      : []),
    {
      name: t('company_info'),
      icon: Building2,
      href: '/companies?status=true',
    },
    {
      name: t('user_explorer'),
      icon: CircleUserRound,
      href: '/users',
    },
    // Investor product metrics are cross-customer data: super admins only, like the API.
    ...(isSuperAdmin
      ? [
          {
            name: t('product_metrics'),
            icon: ChartLine,
            href: '/product-metrics',
          },
        ]
      : []),
    // CV scan reports hold candidate data: super admins only, like the API.
    ...(isSuperAdmin
      ? [{ name: t('scan_reports'), icon: ScanSearch, href: '/scan-reports' }]
      : []),
    { name: t('settings'), icon: Settings, href: '/settings' },
  ];

  return (
    <>
      {!isCollapsed && (
        <button
          type="button"
          className={styles.backdrop}
          aria-label={t('close_navigation')}
          onClick={onToggle}
        />
      )}
      <aside
        id="admin-sidebar"
        className={cn(styles.sidebar, isCollapsed && styles.collapsed)}
        aria-label={t('navigation')}
      >
        <div className={styles.header}>
          <Link
            className={styles.brand}
            to={isSuperAdmin ? '/' : '/companies?status=true'}
            onClick={handleNavigation}
            aria-label={t('home')}
          >
            <img
              src={isCollapsed ? KollabHRLogo : KollabHRFullLogo}
              alt="KollabHR"
            />
          </Link>
        </div>
        {!isCollapsed && (
          <div className={styles.context}>
            <span className={styles.roleDot} aria-hidden="true" />
            <span>{t(isSuperAdmin ? 'super_admin' : 'admin')}</span>
          </div>
        )}
        <nav className={styles.navigation} aria-label={t('navigation')}>
          {!isCollapsed && <p className={styles.navLabel}>{t('workspace')}</p>}
          {navigation.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.end}
              onClick={handleNavigation}
              title={isCollapsed ? item.name : undefined}
              aria-label={isCollapsed ? item.name : undefined}
              className={({ isActive }) =>
                cn(styles.navLink, isActive && styles.active)
              }
            >
              <item.icon className={styles.icon} aria-hidden="true" />
              {!isCollapsed && <span>{item.name}</span>}
            </NavLink>
          ))}
        </nav>
        <div className={styles.footer}>
          <Button
            type="button"
            variant="ghost"
            className={styles.footerButton}
            onClick={onToggle}
            aria-label={t(
              isCollapsed ? 'expand_navigation' : 'collapse_navigation'
            )}
            aria-controls="admin-sidebar"
            aria-expanded={!isCollapsed}
            title={isCollapsed ? t('expand_navigation') : undefined}
          >
            {isCollapsed ? (
              <PanelLeftOpen className={styles.icon} aria-hidden="true" />
            ) : (
              <PanelLeftClose className={styles.icon} aria-hidden="true" />
            )}
            {!isCollapsed && <span>{t('collapse_navigation')}</span>}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className={styles.footerButton}
            onClick={openLogoutConfirmationDialog}
            aria-label={t('logout')}
            title={isCollapsed ? t('logout') : undefined}
          >
            <LogOut className={styles.icon} aria-hidden="true" />
            {!isCollapsed && <span>{t('logout')}</span>}
          </Button>
        </div>
      </aside>
    </>
  );
}
